/**
 * 🧍 AvatarBuilder — step-by-step collaborative avatar wizard.
 *
 * Design pillar (owner canon Feb 2026): every accessibility dimension is a
 * real slider. Wheelchair, prosthetics, dexterity, sensory, dominant hand.
 * NOT a cookie-cutter template.
 *
 * Flow: Start → Baseline (log-pull) → Physical → Accessibility → Style → Save.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { AvatarPreview } from "@/components/AvatarPreview";
import { ChevronLeft, ChevronRight, Sparkles, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["start", "baseline", "physical", "accessibility", "style", "review"];

/* ─────────────────── Field option catalogs (owner-locked) ─────────────────── */
const BODY_BUILDS = ["slim", "average", "muscular", "powerful"];
const MOBILITY_AIDS = [
  { id: "none",              label: "None" },
  { id: "manual_wheelchair", label: "Manual wheelchair" },
  { id: "power_wheelchair",  label: "Power wheelchair" },
  { id: "walker",            label: "Walker" },
  { id: "cane",              label: "Cane" },
  { id: "crutches",          label: "Crutches" },
  { id: "prosthetic_leg",    label: "Prosthetic leg" },
  { id: "other",             label: "Other" },
];
const DOMINANT_HANDS = ["right", "left", "ambidextrous"];
const GRIP_STRENGTHS = ["low", "typical", "high"];
const SKIN_TONES = ["light", "medium_light", "medium", "medium_dark", "dark", "deep"];
const HAIR_STYLES = ["bald", "shaved", "short", "medium", "long"];
const HAIR_COLORS = ["black", "brown", "chestnut", "auburn", "blond", "gray", "white", "red", "blue", "pink", "green", "violet"];
const PROSTHETIC_LOCATIONS = ["left_arm", "right_arm", "left_leg", "right_leg"];
const PROSTHETIC_TYPES = ["bionic", "myoelectric", "passive", "running_blade", "socket", "custom"];

// Inclusive, multi-select self-identification — users may pick zero, one, or
// many. "Prefer not to say" is a first-class option. "Custom" opens a free-
// text field so no one is forced into a bucket they don't belong to.
const ETHNICITIES = [
  { id: "african",              label: "African" },
  { id: "african_diaspora",     label: "African Diaspora / Black" },
  { id: "east_asian",           label: "East Asian" },
  { id: "southeast_asian",      label: "Southeast Asian" },
  { id: "south_asian",          label: "South Asian" },
  { id: "central_asian",        label: "Central Asian" },
  { id: "mena",                 label: "Middle Eastern / North African" },
  { id: "latin_american",       label: "Latin American / Hispanic" },
  { id: "caribbean",            label: "Caribbean" },
  { id: "indigenous_americas",  label: "Indigenous · Americas" },
  { id: "pacific_islander",     label: "Pacific Islander" },
  { id: "aboriginal_oceanic",   label: "Aboriginal · Oceanic" },
  { id: "european",             label: "European / White" },
  { id: "jewish",               label: "Jewish" },
  { id: "roma",                 label: "Roma" },
  { id: "mixed",                label: "Mixed / Multi-ethnic" },
  { id: "custom",               label: "Custom (type below)" },
  { id: "prefer_not_to_say",    label: "Prefer not to say" },
];

/* ─────────────────── Root component ─────────────────── */
export default function AvatarBuilderPage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [baselinePreview, setBaselinePreview] = useState(null);

  // Load existing avatar + default roster
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/avatar`, { withCredentials: true }),
          axios.get(`${API}/avatar/default-roster`),
        ]);
        setAvatar(r1.data);
        setDefaults(r2.data?.roster || []);
      } catch (err) {
        // Unauthenticated — still show defaults + let them play with the builder
        try {
          const r2 = await axios.get(`${API}/avatar/default-roster`);
          setDefaults(r2.data?.roster || []);
        } catch {/* ignore */}
        setAvatar({ exists: false, physical: {}, accessibility: {}, style: {} });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = (path, value) => {
    setAvatar((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const [head, ...rest] = path.split(".");
      if (rest.length === 0) {
        next[head] = value;
      } else {
        next[head] = { ...(next[head] || {}), [rest.join(".")]: value };
      }
      return next;
    });
  };

  const pullBaseline = async () => {
    try {
      const { data } = await axios.post(`${API}/avatar/baseline-from-logs`, {}, { withCredentials: true });
      setBaselinePreview(data);
    } catch (err) {
      toast.error("Sign in to pull baseline from your logs.");
    }
  };

  const applyBaseline = () => {
    if (!baselinePreview) return;
    setAvatar((prev) => ({
      ...prev,
      physical: { ...(prev?.physical || {}), ...(baselinePreview.physical || {}) },
      baseline_source: "auto_from_logs",
    }));
    toast.success("Baseline applied — tweak anything below.");
  };

  const pickDefault = (d) => {
    setAvatar((prev) => ({
      ...prev,
      builder_type: "quick_default",
      default_pick: d.id,
    }));
    setStep(STEPS.indexOf("review"));
  };

  const startCustom = () => {
    setAvatar((prev) => ({
      ...prev,
      builder_type: "custom_2d",
      default_pick: null,
    }));
    setStep(STEPS.indexOf("baseline"));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        builder_type: avatar.builder_type || "custom_2d",
        physical: avatar.physical || {},
        accessibility: avatar.accessibility || {},
        style: avatar.style || {},
        baseline_source: avatar.baseline_source || "manual",
        default_pick: avatar.default_pick || null,
      };
      await axios.post(`${API}/avatar`, payload, { withCredentials: true });
      toast.success("Avatar saved. It follows you into the gym and kitchen.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed — sign in first.");
    } finally {
      setSaving(false);
    }
  };

  const canBack = step > 0;
  const canNext = step < STEPS.length - 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin" size={22} />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="avatar-builder-page">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 space-y-6">
        {/* Header + progress */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Avatar Builder · 1:1</div>
            <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Anton, sans-serif" }}>YOUR REFLECTION</h1>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-neutral-400">
            {STEPS.map((s, i) => (
              <div key={s} className={`px-2 py-1 rounded ${i === step ? "bg-amber-500 text-black font-bold" : i < step ? "bg-emerald-800/60 text-emerald-200" : "bg-neutral-900 text-neutral-500"}`}>
                {i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Two-column: left = current step form, right = live preview */}
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur p-5 sm:p-6">
            {step === 0 && <StepStart defaults={defaults} onCustom={startCustom} onPickDefault={pickDefault} />}
            {step === 1 && <StepBaseline baselinePreview={baselinePreview} onPull={pullBaseline} onApply={applyBaseline} avatar={avatar} patch={patch} />}
            {step === 2 && <StepPhysical avatar={avatar} patch={patch} />}
            {step === 3 && <StepAccessibility avatar={avatar} patch={patch} />}
            {step === 4 && <StepStyle avatar={avatar} patch={patch} />}
            {step === 5 && <StepReview avatar={avatar} onSave={save} saving={saving} />}
          </div>

          {/* Live preview */}
          <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-neutral-900/50 to-black p-5">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Live preview</div>
            <AvatarPreview params={avatar} size={280} />
            <PreviewCaption avatar={avatar} />
          </div>
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            disabled={!canBack}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-white/80 hover:text-white hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="avatar-back-btn"
          >
            <ChevronLeft size={16} /> Back
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="avatar-next-btn"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* ─────────────────── Step 0 · Start ─────────────────── */
function StepStart({ defaults, onCustom, onPickDefault }) {
  return (
    <div className="space-y-5" data-testid="avatar-step-start">
      <h2 className="text-xl font-bold">Start your reflection</h2>
      <p className="text-sm text-neutral-400">Build a custom 1:1 avatar with full accessibility controls — or pick one of the four defaults to begin.</p>
      <button
        onClick={onCustom}
        className="w-full text-left group px-4 py-4 rounded-xl border border-amber-500/40 bg-amber-950/20 hover:bg-amber-950/40 transition"
        data-testid="avatar-start-custom"
      >
        <div className="flex items-center gap-3">
          <Sparkles size={20} className="text-amber-300" />
          <div>
            <div className="font-bold text-amber-200">Build custom (full control)</div>
            <div className="text-xs text-neutral-400">Every slider, every accessibility axis, exactly your reality.</div>
          </div>
        </div>
      </button>
      <div className="text-xs uppercase tracking-widest text-neutral-500 pt-2">Or start from a default</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {defaults.map((d) => (
          <button
            key={d.id}
            onClick={() => onPickDefault(d)}
            className="text-left rounded-xl border border-white/10 bg-neutral-950 hover:bg-neutral-900 hover:border-white/25 p-4 transition"
            data-testid={`avatar-default-${d.id}`}
          >
            <div className="font-bold">{d.name}</div>
            <div className="text-[11px] text-neutral-500 uppercase tracking-widest mt-0.5">{d.gender_expression}</div>
            <div className="text-xs text-neutral-400 mt-2">{d.preview_hint}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── Step 1 · Baseline pull ─────────────────── */
function StepBaseline({ baselinePreview, onPull, onApply, avatar, patch }) {
  const applied = avatar?.baseline_source === "auto_from_logs";
  const snap = baselinePreview?.profile_snapshot || {};
  const ai = baselinePreview?.ai_coach_snapshot || {};
  const sig = baselinePreview?.signals || {};
  return (
    <div className="space-y-5" data-testid="avatar-step-baseline">
      <h2 className="text-xl font-bold">Everything the app already knows about you</h2>
      <p className="text-sm text-neutral-400">Pull height, weight, body-build, and training experience from your profile · Daily Vitals · workout logs · AI Coach plans. Review, then apply.</p>

      <button
        onClick={onPull}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-black font-bold"
        data-testid="avatar-pull-baseline"
      >
        <Sparkles size={14} /> Pull from my profile
      </button>

      {baselinePreview && (
        <div className="space-y-4" data-testid="avatar-baseline-preview">
          {/* Physical derivation */}
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4 space-y-2">
            <div className="text-xs uppercase tracking-widest text-emerald-300">Derived physical</div>
            <BaselineRow label="Height"      value={baselinePreview.physical?.height_cm ? `${baselinePreview.physical.height_cm} cm` : "—"} />
            <BaselineRow label="Weight"      value={baselinePreview.physical?.weight_kg ? `${baselinePreview.physical.weight_kg} kg` : "—"} />
            <BaselineRow label="Muscle tone" value={baselinePreview.physical?.muscle_tone ? `${baselinePreview.physical.muscle_tone} / 5` : "—"} />
            <BaselineRow label="Body build"  value={baselinePreview.physical?.body_build || "—"} />
          </div>

          {/* Profile snapshot */}
          {(snap.name || snap.age || snap.hero_class) && (
            <div className="rounded-xl border border-sky-500/30 bg-sky-950/20 p-4 space-y-2">
              <div className="text-xs uppercase tracking-widest text-sky-300">From your profile</div>
              {snap.name       && <BaselineRow label="Name"       value={snap.name} />}
              {snap.handle     && <BaselineRow label="Handle"     value={`@${snap.handle}`} />}
              {snap.age        && <BaselineRow label="Age"        value={snap.age} />}
              {snap.sex        && <BaselineRow label="Sex"        value={snap.sex} />}
              {snap.goal       && <BaselineRow label="Goal"       value={snap.goal} />}
              {snap.hero_class && <BaselineRow label="Hero class" value={`${snap.hero_class}${snap.hero_level ? ` · lv ${snap.hero_level}` : ""}`} />}
              {snap.spawn_area && <BaselineRow label="Home"       value={snap.spawn_area} />}
            </div>
          )}

          {/* AI Coach snapshot */}
          {(ai.body_type || ai.experience || ai.goal) && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4 space-y-2">
              <div className="text-xs uppercase tracking-widest text-amber-300">From AI Coach</div>
              {ai.body_type     && <BaselineRow label="Body type"      value={ai.body_type} />}
              {ai.experience    && <BaselineRow label="Experience"     value={ai.experience} />}
              {ai.goal          && <BaselineRow label="Coach goal"     value={ai.goal} />}
              {ai.days_per_week && <BaselineRow label="Days per week"  value={String(ai.days_per_week)} />}
              {ai.notes         && <div className="text-xs italic text-amber-200/80 border-t border-amber-500/20 pt-2">&ldquo;{ai.notes}&rdquo;</div>}
            </div>
          )}

          {/* Activity signals — read-only summary */}
          <div className="rounded-xl border border-white/10 bg-neutral-950/60 p-4 space-y-1.5">
            <div className="text-xs uppercase tracking-widest text-neutral-400">Activity signals</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <SigRow k="Cardio sessions"       v={sig.cardio_sessions} />
              <SigRow k="Interactive gym"       v={sig.interactive_gym_sessions} />
              <SigRow k="Strength sessions"     v={sig.strength_sessions} />
              <SigRow k="Weekly workout logs"   v={sig.user_workouts} />
              <SigRow k="Kitchen sessions"      v={sig.interactive_kitchen_sessions} />
              <SigRow k="Food-log entries"      v={sig.food_log_entries} />
              <SigRow k="AI Coach plans"        v={sig.ai_plans_total} />
              <SigRow k="Sign-in streak"        v={sig.signin_streak_current} />
              <SigRow k="Food streak"           v={sig.food_streak_current} />
              <SigRow k="Total activity score"  v={sig.total_activity_score} />
            </div>
          </div>

          <button
            onClick={onApply}
            disabled={applied}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-50"
            data-testid="avatar-apply-baseline"
          >
            {applied ? <><Check size={14} /> Applied — tweak anytime below</> : "Apply this baseline"}
          </button>
        </div>
      )}

      <div className="text-xs text-neutral-500 pt-2 border-t border-white/5">Prefer manual? Skip this step — every field is still editable ahead.</div>
    </div>
  );
}
function BaselineRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-neutral-400">{label}</span>
      <span className="font-mono truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}
function SigRow({ k, v }) {
  const hasVal = v !== null && v !== undefined && v !== "";
  return (
    <div className="flex items-center justify-between text-neutral-400">
      <span className="truncate">{k}</span>
      <span className={`font-mono ${hasVal ? "text-white" : "text-neutral-600"}`}>{hasVal ? String(v) : "—"}</span>
    </div>
  );
}

/* ─────────────────── Step 2 · Physical ─────────────────── */
function StepPhysical({ avatar, patch }) {
  const p = avatar?.physical || {};
  return (
    <div className="space-y-5" data-testid="avatar-step-physical">
      <h2 className="text-xl font-bold">Physical</h2>

      <Slider label="Height (cm)" min={130} max={220} step={1} value={p.height_cm || 170}
              onChange={(v) => patch("physical.height_cm", v)} testId="avatar-slider-height" />
      <Slider label="Weight (kg)" min={35} max={200} step={1} value={p.weight_kg || 70}
              onChange={(v) => patch("physical.weight_kg", v)} testId="avatar-slider-weight" />
      <Slider label="Muscle tone" min={1} max={5} step={1} value={p.muscle_tone || 3}
              onChange={(v) => patch("physical.muscle_tone", v)} testId="avatar-slider-tone" />

      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Body build</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BODY_BUILDS.map((b) => (
            <button
              key={b}
              onClick={() => patch("physical.body_build", b)}
              className={`px-3 py-2 rounded text-sm capitalize ${p.body_build === b ? "bg-amber-500 text-black font-bold" : "bg-neutral-900 hover:bg-neutral-800 text-neutral-300"}`}
              data-testid={`avatar-build-${b}`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Step 3 · Accessibility ─────────────────── */
function StepAccessibility({ avatar, patch }) {
  const a = avatar?.accessibility || {};
  const prosth = a.prosthetics || [];
  const toggleLoc = (loc, type) => {
    const has = prosth.find((x) => x.location === loc);
    const next = has ? prosth.filter((x) => x.location !== loc) : [...prosth, { location: loc, type: type || "custom" }];
    patch("accessibility.prosthetics", next);
  };
  const changeType = (loc, type) => {
    const next = prosth.map((x) => (x.location === loc ? { ...x, type } : x));
    patch("accessibility.prosthetics", next);
  };

  return (
    <div className="space-y-5" data-testid="avatar-step-accessibility">
      <h2 className="text-xl font-bold">Accessibility</h2>

      <ChoiceGrid label="Mobility aid" options={MOBILITY_AIDS} value={a.mobility_aid || "none"}
                  onChange={(v) => patch("accessibility.mobility_aid", v)} testPrefix="avatar-mobility" />

      <ChoiceGrid label="Dominant hand" options={DOMINANT_HANDS.map((h) => ({ id: h, label: h }))} value={a.dominant_hand || "right"}
                  onChange={(v) => patch("accessibility.dominant_hand", v)} testPrefix="avatar-hand" />

      <ChoiceGrid label="Grip strength" options={GRIP_STRENGTHS.map((g) => ({ id: g, label: g }))} value={a.grip_strength || "typical"}
                  onChange={(v) => patch("accessibility.grip_strength", v)} testPrefix="avatar-grip" />

      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Prosthetics · limb differences</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {PROSTHETIC_LOCATIONS.map((loc) => {
            const entry = prosth.find((x) => x.location === loc);
            const active = !!entry;
            return (
              <div key={loc} className={`rounded-lg border ${active ? "border-amber-500/60 bg-amber-950/20" : "border-white/10 bg-neutral-950"} p-3`}>
                <button
                  onClick={() => toggleLoc(loc)}
                  className="flex items-center justify-between w-full text-left"
                  data-testid={`avatar-prosth-${loc}`}
                >
                  <span className="capitalize">{loc.replace("_", " ")}</span>
                  <span className={`text-xs uppercase tracking-widest ${active ? "text-amber-300" : "text-neutral-500"}`}>{active ? "On" : "Off"}</span>
                </button>
                {active && (
                  <div className="mt-2 grid grid-cols-3 gap-1">
                    {PROSTHETIC_TYPES.map((t) => (
                      <button
                        key={t}
                        onClick={() => changeType(loc, t)}
                        className={`text-[11px] px-2 py-1 rounded capitalize ${entry?.type === t ? "bg-amber-500 text-black font-bold" : "bg-neutral-900 hover:bg-neutral-800 text-neutral-400"}`}
                      >
                        {t.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Sensory profile</div>
        <Toggle label="Sound sensitive" value={!!a.sensory_profile?.sound_sensitive}
                onChange={(v) => patch("accessibility.sensory_profile", { ...(a.sensory_profile || {}), sound_sensitive: v })}
                testId="avatar-sensory-sound" />
        <Toggle label="Light sensitive" value={!!a.sensory_profile?.light_sensitive}
                onChange={(v) => patch("accessibility.sensory_profile", { ...(a.sensory_profile || {}), light_sensitive: v })}
                testId="avatar-sensory-light" />
        <Toggle label="Prefer high-contrast UI" value={!!a.sensory_profile?.high_contrast_preferred}
                onChange={(v) => patch("accessibility.sensory_profile", { ...(a.sensory_profile || {}), high_contrast_preferred: v })}
                testId="avatar-sensory-contrast" />
      </div>
    </div>
  );
}

/* ─────────────────── Step 4 · Style ─────────────────── */
function StepStyle({ avatar, patch }) {
  const s = avatar?.style || {};
  const ethn = s.ethnicity || [];
  const toggleEth = (id) => {
    const next = ethn.includes(id) ? ethn.filter((x) => x !== id) : [...ethn, id];
    patch("style.ethnicity", next);
  };
  const clearCustomSkin = () => patch("style.skin_hex", null);
  const clearCustomHair = () => patch("style.hair_hex", null);

  return (
    <div className="space-y-5" data-testid="avatar-step-style">
      <h2 className="text-xl font-bold">Style</h2>

      {/* Ethnicity — multi-select, optional */}
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Heritage / ethnicity <span className="normal-case tracking-normal text-neutral-600">(pick any, or none)</span></div>
        <div className="flex flex-wrap gap-2">
          {ETHNICITIES.map((e) => (
            <button
              key={e.id}
              onClick={() => toggleEth(e.id)}
              className={`px-3 py-1.5 rounded text-sm ${ethn.includes(e.id) ? "bg-amber-500 text-black font-bold" : "bg-neutral-900 hover:bg-neutral-800 text-neutral-300"}`}
              data-testid={`avatar-ethnicity-${e.id}`}
            >
              {e.label}
            </button>
          ))}
        </div>
        {ethn.includes("custom") && (
          <input
            type="text"
            value={s.ethnicity_custom || ""}
            onChange={(e) => patch("style.ethnicity_custom", e.target.value)}
            placeholder="Describe in your own words"
            className="mt-3 w-full px-3 py-2 rounded bg-neutral-950 border border-white/10 text-sm text-white focus:border-amber-500/60 focus:outline-none"
            data-testid="avatar-ethnicity-custom-input"
          />
        )}
      </div>

      {/* Skin — preset swatches + full color wheel */}
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Skin tone</div>
        <div className="flex flex-wrap items-center gap-2">
          {SKIN_TONES.map((t) => (
            <button
              key={t}
              onClick={() => { patch("style.skin_tone", t); patch("style.skin_hex", null); }}
              className={`w-9 h-9 rounded-full border-2 ${!s.skin_hex && s.skin_tone === t ? "border-amber-400 scale-110" : "border-white/10"} transition`}
              style={{ backgroundColor: skinSwatch(t) }}
              data-testid={`avatar-skin-${t}`}
              aria-label={t}
              title={t}
            />
          ))}
          <ColorWheel
            label="Custom"
            value={s.skin_hex || skinSwatch(s.skin_tone || "medium")}
            onChange={(hex) => patch("style.skin_hex", hex)}
            onClear={s.skin_hex ? clearCustomSkin : null}
            testId="avatar-skin-wheel"
            active={!!s.skin_hex}
          />
        </div>
      </div>

      <ChoiceGrid label="Hair style" options={HAIR_STYLES.map((h) => ({ id: h, label: h }))} value={s.hair_style || "short"}
                  onChange={(v) => patch("style.hair_style", v)} testPrefix="avatar-hair-style" />

      {/* Hair — preset swatches + full color wheel */}
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Hair color</div>
        <div className="flex flex-wrap items-center gap-2">
          {HAIR_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => { patch("style.hair_color", c); patch("style.hair_hex", null); }}
              className={`w-8 h-8 rounded-full border-2 ${!s.hair_hex && s.hair_color === c ? "border-amber-400 scale-110" : "border-white/10"} transition`}
              style={{ backgroundColor: hairSwatch(c) }}
              data-testid={`avatar-hair-color-${c}`}
              aria-label={c}
              title={c}
            />
          ))}
          <ColorWheel
            label="Custom"
            value={s.hair_hex || hairSwatch(s.hair_color || "brown")}
            onChange={(hex) => patch("style.hair_hex", hex)}
            onClear={s.hair_hex ? clearCustomHair : null}
            testId="avatar-hair-wheel"
            active={!!s.hair_hex}
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── ColorWheel — native browser picker w/ clear ─────────────────── */
function ColorWheel({ label, value, onChange, onClear, testId, active }) {
  return (
    <div className="flex items-center gap-1.5">
      <label className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full border-2 cursor-pointer transition ${active ? "border-amber-400 scale-110" : "border-white/10 hover:border-white/30"}`}
             style={{ background: active ? value : "conic-gradient(from 0deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" }}
             title={label}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          data-testid={testId}
        />
      </label>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
          data-testid={`${testId}-clear`}
        >
          clear
        </button>
      )}
    </div>
  );
}

/* ─────────────────── Step 5 · Review ─────────────────── */
function StepReview({ avatar, onSave, saving }) {
  return (
    <div className="space-y-5" data-testid="avatar-step-review">
      <h2 className="text-xl font-bold">Ready to go?</h2>
      <p className="text-sm text-neutral-400">Your avatar will walk with you into the interactive gym, kitchen, and every session.</p>
      <div className="rounded-lg border border-white/10 bg-neutral-950 p-4 text-sm space-y-2">
        <ReviewRow k="Builder" v={avatar?.builder_type || "custom_2d"} />
        <ReviewRow k="Height" v={avatar?.physical?.height_cm ? `${avatar.physical.height_cm} cm` : "—"} />
        <ReviewRow k="Weight" v={avatar?.physical?.weight_kg ? `${avatar.physical.weight_kg} kg` : "—"} />
        <ReviewRow k="Build" v={avatar?.physical?.body_build || "—"} />
        <ReviewRow k="Mobility aid" v={avatar?.accessibility?.mobility_aid || "none"} />
        <ReviewRow k="Dominant hand" v={avatar?.accessibility?.dominant_hand || "right"} />
        <ReviewRow k="Prosthetics" v={(avatar?.accessibility?.prosthetics || []).length ? avatar.accessibility.prosthetics.map((x) => `${x.location.replace("_", " ")} (${x.type})`).join(", ") : "none"} />
        <ReviewRow k="Heritage" v={
          (avatar?.style?.ethnicity || []).length
            ? avatar.style.ethnicity.map((e) => e === "custom" ? (avatar.style.ethnicity_custom || "custom") : (ETHNICITIES.find((x) => x.id === e)?.label || e)).join(", ")
            : "—"
        } />
        <ReviewRow k="Skin"  v={avatar?.style?.skin_hex ? `custom ${avatar.style.skin_hex}` : (avatar?.style?.skin_tone || "medium")} />
        <ReviewRow k="Hair"  v={avatar?.style?.hair_hex ? `custom ${avatar.style.hair_hex}` : (avatar?.style?.hair_color || "brown")} />
        <ReviewRow k="Baseline source" v={avatar?.baseline_source || "manual"} />
      </div>
      <button
        onClick={onSave}
        disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-black tracking-widest disabled:opacity-50"
        data-testid="avatar-save-btn"
      >
        {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
        {saving ? "Saving…" : "Save my avatar"}
      </button>
    </div>
  );
}
function ReviewRow({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-500 text-xs uppercase tracking-widest">{k}</span>
      <span className="font-mono">{String(v)}</span>
    </div>
  );
}

/* ─────────────────── Shared UI atoms ─────────────────── */
function Slider({ label, min, max, step, value, onChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-neutral-300">{label}</span>
        <span className="font-mono text-sm text-amber-300">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-amber-500"
        data-testid={testId}
      />
    </div>
  );
}
function ChoiceGrid({ label, options, value, onChange, testPrefix }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className={`px-3 py-1.5 rounded text-sm capitalize ${value === o.id ? "bg-amber-500 text-black font-bold" : "bg-neutral-900 hover:bg-neutral-800 text-neutral-300"}`}
            data-testid={`${testPrefix}-${o.id}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
function Toggle({ label, value, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className="w-full flex items-center justify-between px-3 py-2 rounded bg-neutral-900 hover:bg-neutral-800 mb-1.5"
      data-testid={testId}
    >
      <span className="text-sm">{label}</span>
      <span className={`text-xs uppercase tracking-widest ${value ? "text-amber-300" : "text-neutral-500"}`}>{value ? "On" : "Off"}</span>
    </button>
  );
}

function PreviewCaption({ avatar }) {
  const bits = [];
  if (avatar?.physical?.height_cm) bits.push(`${avatar.physical.height_cm}cm`);
  if (avatar?.physical?.body_build) bits.push(avatar.physical.body_build);
  if (avatar?.accessibility?.mobility_aid && avatar.accessibility.mobility_aid !== "none") bits.push(avatar.accessibility.mobility_aid.replace(/_/g, " "));
  if ((avatar?.accessibility?.prosthetics || []).length) bits.push(`${avatar.accessibility.prosthetics.length} prosthetic${avatar.accessibility.prosthetics.length > 1 ? "s" : ""}`);
  return <div className="text-[11px] text-neutral-500 mt-3 text-center italic">{bits.join(" · ") || "Adjust the sliders — the preview updates live."}</div>;
}

/* ─────────────────── Swatches (must match AvatarPreview) ─────────────────── */
function skinSwatch(t) {
  return { light: "#f4d0b0", medium_light: "#e0b48c", medium: "#c08862", medium_dark: "#8a5a3b", dark: "#5c3820", deep: "#3a2213" }[t];
}
function hairSwatch(c) {
  return { black: "#1a1a1a", brown: "#5a3721", chestnut: "#7a4b2a", auburn: "#8a3f1f", blond: "#d4b062", gray: "#8a8a8a", white: "#e8e8e8", red: "#a83a1c", blue: "#3455a3", pink: "#d97aa7", green: "#3a8a55", violet: "#8a4bb0" }[c];
}
